import {
  CAAS_INDEX_SUFFIX,
  DISABLE_ADS,
  IS_DEV_SITE,
} from "@src/app/constants";
import {
  GA5_REF_CODE_CONTENTHUB_3TITLES,
  GA5_REF_CODE_CONTENTHUB_ANCHOR,
  GA5_REF_CODE_CONTENTHUB_BLOCK,
} from "@src/app/utils/ga/ref-code";
import {
  BRIGHTCOVE_CONFIG,
  DEFAULT_CONFIG_GAM,
  LOTAME_CONFIG,
  VWO_CONFIG,
} from "@src/pubs/common/config";

import {
  ConfigAboutUs,
  ConfigAffiliateLibrary,
  ConfigAffiliatesDeclaration,
  ConfigAffiliateShopping,
  ConfigAffiliatesProduct,
  ConfigAmazonAffiliates,
  ConfigAuthorProfile,
  ConfigBrightcove,
  ConfigCaas,
  ConfigCategory,
  ConfigContentHub,
  ConfigError,
  ConfigExploreMore,
  ConfigFloatingCta,
  ConfigGAM,
  ConfigGTM,
  ConfigHome,
  ConfigLatestStory,
  ConfigLotame,
  ConfigNewsletter,
  ConfigRss,
  ConfigSearch,
  ConfigSingleStory,
  ConfigSite,
  ConfigSkimlinks,
  ConfigTaboola,
  ConfigTag,
  ConfigTeaser,
  ConfigTrackingCode,
  ConfigVideoLanding,
  ConfigVideoOverlayPortrait,
  ConfigVWO,
} from "../types/config";
import joinOurVillageTag from "./assets/joinourvillage-tag.png";
import ContentListCarousel from "./components/ContentListCarousel";
import { chevronLeftIcon, chevronRightIcon } from "./icons/chevronIcon";
import { moreIcon } from "./icons/moreIcon";
import {
  emailIcon,
  facebookIcon,
  linkedInIcon,
  telegramIcon,
  whatsappIcon,
  xIcon,
} from "./icons/socialIcon";
import logoPath from "./logo.svg?url";
import newsletterbg from "./newsletter.webp";
import callQuoteSvg from "./quote.svg?url";

export const configSite: ConfigSite = {
  name: "The Singapore Women's Weekly ",
  address:
    import.meta.env.VITE_PUBLIC_BASE_URL || "https://www.womensweekly.com.sg", // without trailing slash
  tagline: "Home",
  description:
    "Stay updated with parenting news, lifestyle tips, and working mum hacks in Singapore, from family life to self-care.",
  titleSeparator: "—",
  logoPath,
  sectionLevel3: true,
  pageShare: {
    size: 35,
    media: [
      {
        name: "Telegram",
        icon: telegramIcon,
      },
      {
        name: "Facebook",
        icon: facebookIcon,
      },
      {
        name: "Whatsapp",
        icon: whatsappIcon,
      },
      {
        name: "Email",
        icon: emailIcon,
      },
      {
        name: "LinkedIn",
        icon: linkedInIcon,
      },
      {
        name: "X",
        icon: xIcon,
      },
    ],
  },
  aboutUs: {
    headlineBelow: true,
  },
  links: [
    {
      rel: "icon",
      type: "image/png",
      href: "/favicon-96x96.png",
      sizes: "96x96",
    },
    { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
    { rel: "shortcut icon", href: "/favicon.ico" },
  ],
};

export { configHeader } from "./config/configHeader";

export const configFloatingCta: ConfigFloatingCta = {
  display: true,
  type: "image",
  ariaLabel: "Join our Village",
  placement: "bottom-right",
  image: {
    src: joinOurVillageTag,
    srcWidth: 150,
    srcHeight: 150,
    alt: "Join our Village",
  },
  href: "https://www.womensweekly.com.sg/parenting/sww-village-mums-community-singapore?utm_source=web&utm_medium=sticky+cta&utm_campaign=sww+village+article",
  target: "_blank",
  skipPaths: [],
};

export const configCaas: ConfigCaas = {
  index: `womensweekly${CAAS_INDEX_SUFFIX}`,
  tagIdPrefix: "tag:womensweekly-keywords@sph.com.sg,2023:",
  cuePublicationName: "womensweekly",
};

export const configGTM: ConfigGTM = {
  container: "GTM-W97XX82",
  siteVerification: "_cGEdj2ZMeQhb_OTIAKvlCGbSxaBwct6M6XrDxywcXM",
};

export const configGAM: ConfigGAM = {
  ...DEFAULT_CONFIG_GAM,
  account: "/5908/sww",
  debounce: true,
  debounceDelay: 300,
  enableAbm: false,
};

export const configLotame: ConfigLotame = {
  ...LOTAME_CONFIG,
  active: !DISABLE_ADS,
};

export const configSkimlinks: ConfigSkimlinks = {
  active: !DISABLE_ADS,
  skimlinkId: "157725X1632719",
  geniuslinkId: "319905",
};

export const configVWO: ConfigVWO = {
  ...VWO_CONFIG,
  active: true,
};

export const configBrightcove: ConfigBrightcove = {
  ...BRIGHTCOVE_CONFIG,
};

export const configSingleStory: ConfigSingleStory = {
  authorProfile: true,
  caption: {
    prefix: "Credit: ",
  },
  byline: { defaultCredit: "The Weekly" },
  configShareArticle: {
    label: "Share this article",
    divider: {
      top: false,
    },
  },
  featureStory: {
    skipCoverCaption: true,
    bodyMaxWidth: 830,
  },
  gallery: {
    paginationSeparator: "/",
    galleryItemShareMedia: ["Facebook", "X", "Whatsapp", "Pinterest"],
  },
  displayTags: {
    displayBottomTags: true,
    displayTagHeading: true,
  },
  productContent: {
    hidePriceInButton: true,
    showBestPrice: true,
  },
  quote: {
    svg: callQuoteSvg,
  },
};

export const configContentHub: ConfigContentHub = {
  configAds: {
    showInlineAds: true,
  },
  configMasthead: {
    heading: {
      display: false,
    },
  },
  configBodyContent: {
    withHeading: true,
    display: true,
  },
  configShareButton: {
    display: true,
    label: "Share this page",
  },
  configEditorsNote: {
    display: false,
  },
  configCallQoute: {
    display: false,
  },
  configFeaturedSection: {
    display: true,
    limit: 4,
  },
  configDeals: {
    display: false,
    props: {
      heading: "editor's picks",
      noOfItemsInFrame: 4,
    },
  },
  configTopStories: {
    display: true,
    firstItemAnchorBlock: true,
    fallback: true,
  },
  socialVideo: {
    display: false,
  },
  configListingStories: {
    mode: "landscape",
  },
  storiesPerPage: {
    topStories: 0,
    totalStories: 6,
  },
  contenthubBlockF: {
    mode: "landscape",
    mobileMode: "landscape",
    anchorBlockOnAllScreens: true,
  },
  videoGalleryLandscape: {
    display: true,
    hasPagination: false,
    hasItemDescription: true,
    heading: {
      title: true,
      withDivider: false,
    },
    spotLight: {
      showArrowOnMobile: true,
    },
    strip: {
      dividerVariant: "primary",
    },
  },
  layout: {
    stories: {
      elements: [
        {
          Component: ContentListCarousel,
          name: "contentListBlock",
          key: "topic-0",
          fallback: false,
        },
        { name: "adsMidContent" },
        { name: "videoplaylist" },
      ],
    },
  },
};

export const configLatestStory: ConfigLatestStory = {
  latestStoryLimit: 20,
  subAnchorLimit: 3,
  landscapeIndexes: [0, 1, 2],
  mainContentBlockDisplay: "columns",
  mainContentBlockMode: "original",
  errorLayoutMode: ["landscape", "square", "landscape"],
  portraitImageWidth: 285,
  landscapeImageWidth: 345,
};

export const configCategory: ConfigCategory = {
  pinnedTeasersLimit: 3,
  latestStoriesLimit: 8,
  mainCategoryToAll: true,
  adPositioning: 3,
  card: {
    anchorblock: {
      defaultVariant: "anchorblock-stacked-square3026",
      mobileUp: {
        defaultVariant: "anchorblock-list-mobile-up",
      },
    },
    subAnchor: {
      defaultVariant: "card-list-summary-border-square",
      mobileUp: {
        defaultVariant: "card-list-summary-border-landscape-mobile-up",
      },
    },
    latestArticle: {
      defaultVariant: "card-list-square",
      mobileUp: {
        defaultVariant: "card-stacked-original-mobile-up",
      },
    },
  },
};

export const configTag: ConfigTag = {
  topStoriesLimit: 4,
  listedStoriesLimit: 12,
};

export const configAmazonAffiliates: ConfigAmazonAffiliates = {
  partnerTag: IS_DEV_SITE ? "sphmedia-dev-22" : "sphmedia-womensweekly-22",
};

export const configTaboola: ConfigTaboola = {
  enabled: true,
  publisherId: "sphmedia-singaporewomensweekly",
};

export const configSearch: ConfigSearch = {
  mobileMode: "square",
};

export const configExploreMore: ConfigExploreMore = {
  heading: "More Good Stuff",
  limit: 6,
  skipImage: true,
};

export const configVideoLanding: ConfigVideoLanding = {
  videoPlaylistFillUpSize: 9,
  videoPath: "/video",
};

export const configVideoOverlayPortrait: ConfigVideoOverlayPortrait = {};

export const configHome: ConfigHome = {
  headingDivider: false,
  isHeadingClickable: true,
  anchorsWithDescription: ["videoplaylist"],
  viewMore: { icon: moreIcon },
  latestArticle: {
    card: {
      defaultVariant: "card-list-border-square",
      mobileUp: {
        defaultVariant: "card-list-container-border-original-mobile-up",
      },
    },
    viewMore: {
      text: "SEE ALL",
    },
  },
  contentBlockB: {
    cropsByIndex: [
      [0, "landscape"],
      [1, "square"],
      [2, "landscape"],
    ],
    itemCenteredIndex: 1,
    viewMore: {
      text: "SEE ALL",
    },
  },
  contentBlockF: {
    card: {
      defaultVariant: "card-list-summary-border-square",
      mobileUp: {
        defaultVariant: "card-list-summary-border-original-mobile-up",
      },
    },
    skipSectionContext: true,
    landscapeIndexes: [0, 1, 2],
    layoutTwoColumn: [1, 2],
    anchorBlock: {
      index: 0,
      mediaScreen: "all-screen",
    },
    landscapeImageWidth: 630,
    twoColumnsLandscapeImageWidth: 300,
    viewMore: {
      text: "SEE ALL",
    },
  },
  contentBlockD: {
    crop: "portrait",
  },
  contentBlockG: {
    skipSectionContext: true,
    landscapeImageWidth: 300,
    portraitImageWidth: 300,
    card: {
      defaultVariant: "card-list-square",
      mobileUp: {
        defaultVariant: "card-stacked-portrait-mobile-up",
        variantsByIndex: [
          [1, "card-stacked-landscape-mobile-up"],
          [2, "card-stacked-landscape-mobile-up"],
        ],
      },
    },
  },
  contentBlockCarousel: {
    leftIcon: chevronLeftIcon,
    rightIcon: chevronRightIcon,
  },
  videoGalleryLandscape: {
    hasPagination: false,
    strip: {
      icon: true,
      dividerVariant: "primary",
    },
    spotLight: {
      mobileLoopingArrowsBelow: false,
      showArrowOnMobile: true,
    },
    hideIMU: true,
  },
  contentColumnSideScroll: {
    section: "shopping",
    arrowVariant: "drop",
  },
  affiliateProduct: {
    description: "As curated by The Singapore Women’s Weekly team",
  },
  magazineListing: {
    midDivider: true,
  },
  newsletter: {
    containerImageBg: newsletterbg,
    newsletterTitle: `Parents, let's stay sane together.`,
    newsletterBody: `Sign up for our weekly newsletters for the latest deals, tips, and feel-good reads.`,
  },
  trending: {
    limit: 4,
  },
  layoutGrid: {
    elements: [
      {
        name: "anchorBlock",
        dataSource: {
          type: "list",
          key: "anchorblock",
          fallback: "latest",
          limit: 1,
        },
      },
      {
        name: "latestArticle",
        dataSource: {
          type: "list",
          key: "pinned",
          fallback: "latest",
          limit: 4,
        },
      },
      {
        name: "videoGalleryLandscape",
        dataSource: {
          type: "list",
          key: "videoplaylist",
          refCode: "videoplaylist",
          fallback: "videoplaylist",
          limit: 12,
        },
      },
      {
        name: "adUnit",
      },
      {
        name: "contentBlockF",
        dataSource: {
          type: "section",
          key: "parenting",
          limit: 3,
          refCode: "block-f",
        },
      },
      {
        name: "contentBlockD",
        dataSource: {
          type: "list",
          key: "trending",
          fallback: "trending",
          refCode: "trending",
          limit: 4,
        },
        alternateBg: true,
        trendingBlock: true,
        scrollable: true,
      },
      {
        name: "affiliateProduct",
        dataSource: {
          type: "list",
          key: "deals",
        },
      },
      {
        name: "contentBlockF",
        dataSource: {
          type: "section",
          key: "family",
          limit: 3,
          refCode: "block-g",
        },
        alternateBg: true,
        alternateSectionColor: true,
      },
      {
        name: "contentBlockG",
        dataSource: {
          type: "section",
          key: "your-life",
          limit: 4,
          refCode: "block-h",
        },
      },
      {
        name: "contentBlockB",
        dataSource: {
          type: "section",
          key: "food",
          limit: 3,
          refCode: "block-b",
          card: {
            defaultVariant: "card-list-square",
            mobileUp: {
              defaultVariant: "card-stacked-square-mobile-up",
              variantsByIndex: [
                [0, "card-stacked-landscape-mobile-up"],
                [2, "card-stacked-landscape-mobile-up"],
              ],
            },
          },
        },
        alternateBg: true,
      },
      {
        name: "columnSideScroll",
        dataSource: {
          type: "section",
          key: "shopping",
          limit: 10,
          refCode: "column-side-scroll",
        },
      },
      { name: "newsletter" },
    ],
  },
  trendingStories: { publisher: "womensweekly" },
};

export const configNewsletter: ConfigNewsletter = {
  image: newsletterbg,
  title: `Parents, let's stay sane together.`,
  body: `Sign up for our weekly newsletters for the latest deals, tips, and feel-good reads.`,
  subscriptionMessage: {
    title: "Thank you for subscribing!",
    body: "Welcome to our community! You're all set to receive the latest updates, exclusive content, and insights delivered straight to your inbox.\n\nKeep an eye out for your first newsletter hitting your inbox soon!",
  },
};

export const configTeaser: ConfigTeaser = {
  mobileMode: "square",
};

export const configTrackingCode: ConfigTrackingCode = {
  contenthub: {
    anchorBlock: GA5_REF_CODE_CONTENTHUB_ANCHOR,
    topstories: {
      contentBlockF: GA5_REF_CODE_CONTENTHUB_BLOCK,
    },
    listing: GA5_REF_CODE_CONTENTHUB_BLOCK,
    featured: GA5_REF_CODE_CONTENTHUB_3TITLES,
  },
};

export const configAffiliatesDeclaration: ConfigAffiliatesDeclaration = {
  disclaimer:
    "All products have been vetted by the SPH Media shopping team. We may earn an affiliate commission if you buy through our links.",
};

export const configError: ConfigError = {};

export const configAuthorProfile: ConfigAuthorProfile = {
  showReadMore: true,
  hideDivider: true,
  teaserLimit: 14,
  listingLayout: {
    mainContentBlockDisplay: "columns",
    mainContentBlockMode: "original",
  },
};

export const configAboutUs: ConfigAboutUs = {
  profileBlock: {
    heading: "Who We Are",
  },
};

export const configAffiliatesProduct: ConfigAffiliatesProduct = {
  cta: {
    variant: "secondary",
  },
  arrowVariant: "drop",
  originalPriceLabel: "Was ",
};

export const configAffiliateShopping: ConfigAffiliateShopping = {
  withDivider: false,
  layoutGrid: {
    elements: [
      {
        name: "shopTheEdit",
        dataSource: {
          types: ["list"],
          key: "products-7-grid",
          limit: 7,
          config: {
            cta: {
              name: "View more in story",
            },
            anchorBlock: {
              cardLayout: {
                defaultVariant: "anchorblock-overlap-summary",
                mobileUp: {
                  defaultVariant: "anchorblock-overlap-summary-mobile-up",
                },
              },
            },
            moreStories: {
              ads: {
                sidebar: true,
              },
              cardLayout: {
                defaultVariant: "card-stacked-original",
                mobileUp: {
                  defaultVariant:
                    "card-list-summary-border-landscape-mobile-up",
                },
              },
            },
            productMobileLayout: "list",
          },
        },
      },
      {
        name: "products",
        dataSource: {
          types: ["list"],
          key: "products-6-grid",
          limit: 6,
        },
      },
      {
        name: "shopTheEdit",
        dataSource: {
          types: ["section"],
          key: "stories-4",
          limit: 4,
          config: {
            backgroundColor: "#003A3B",
            anchorBlock: {
              cardLayout: {
                defaultVariant: "anchorblock-overlap-summary",
                mobileUp: {
                  defaultVariant: "anchorblock-overlap-summary-mobile-up",
                },
              },
            },
            moreStories: {
              ads: {
                sidebar: true,
              },
              cardLayout: {
                defaultVariant: "card-stacked-original",
                mobileUp: {
                  defaultVariant:
                    "card-list-summary-border-landscape-mobile-up",
                },
              },
            },
          },
        },
      },
      {
        name: "shopTheEdit",
        dataSource: {
          types: ["list"],
          key: "products-4-grid",
          limit: 4,
          config: {
            cta: {
              name: "Shop more in story",
            },
            anchorBlock: {
              cardLayout: {
                defaultVariant: "anchorblock-overlap-summary",
                mobileUp: {
                  defaultVariant: "anchorblock-overlap-summary-mobile-up",
                },
              },
            },
            productMobileLayout: "grid",
          },
        },
      },
      {
        name: "shopTheEdit",
        dataSource: {
          types: ["section", "tag"],
          key: "stories-3",
          limit: 3,
          config: {
            backgroundColor: "#FFFBF0",
            moreStories: {
              isLandscape: true,
              cardLayout: {
                defaultVariant: "card-stacked-original",
                mobileUp: {
                  defaultVariant: "card-stacked-landscape-mobile-up",
                  variantsByIndex: [[1, "card-stacked-square-mobile-up"]],
                },
              },
              layout: {
                mobileUp: "stacked",
              },
            },
          },
        },
      },
      {
        name: "shopTheEdit",
        dataSource: {
          types: ["section", "tag"],
          key: "stories-12",
          limit: 12,
          config: {
            cta: {
              name: "View all deals",
            },
            moreStories: {
              isLandscape: true,
              hasCta: true,
              ads: {
                sidebar: false,
                leaderboard: true,
              },
              cardLayout: {
                defaultVariant: "card-stacked-original",
                mobileUp: {
                  defaultVariant: "card-stacked-original-mobile-up",
                },
              },
              layout: {
                mobileUp: "column",
              },
            },
          },
        },
      },
    ],
  },
  viewMore: { icon: moreIcon },
};
export const configRss: ConfigRss = [];

export const configAffiliateLibrary: ConfigAffiliateLibrary = {
  enable: true,
  arrow: {
    left: chevronLeftIcon,
    right: chevronRightIcon,
  },
};
